// src/services/packProductsService.js
/**
 * Pack Products Service - Handles pack products master management API communication
 * Backend API Base URL: Production VPS via config.js
 * 
 * Pack Products Master System - Manages products sold as packs/bundles
 * Features: Confidence-based pack size inference, manual overrides, verification
 */

import axios from 'axios';
import { NODE_BACKEND_API_URL } from '../config';

const PACK_PRODUCTS_API_URL = `${NODE_BACKEND_API_URL}/pack-products`;

/**
 * @typedef {Object} PackProduct
 * @property {number} id - Record ID (use for API calls)
 * @property {string} product_id - Product ID in Products table
 * @property {string} product_code - Product code
 * @property {string} product_description - Product description
 * @property {string} [ean] - EAN barcode
 * @property {number} [product_cost] - Product cost
 * @property {string} brand_name - Brand name
 * @property {string} category_name - Category name
 * @property {number} inferred_pack_size - Auto-detected pack size
 * @property {number|null} manual_pack_size - Manual override (null = use inferred)
 * @property {number} effective_pack_size - Actual pack size to use
 * @property {'HIGH'|'MEDIUM'|'LOW'} confidence_level - Confidence level
 * @property {number} confidence_pct - Confidence percentage (e.g., 95.50)
 * @property {number} sample_size - Number of sales analyzed
 * @property {boolean} is_verified - Whether verified by user
 * @property {string|null} verified_by - Who verified
 * @property {string|null} verified_at - When verified (ISO date)
 * @property {string|null} notes - Admin notes
 * @property {boolean} [is_active] - Active flag
 * @property {number} current_stock - Current stock (can be negative)
 * @property {number} inferred_unit_count - stock × pack_size
 * @property {0|1} description_mismatch - 1 if pack size not in description
 * @property {string} created_at - Creation date (ISO)
 * @property {string} updated_at - Last update date (ISO)
 */

/**
 * @typedef {Object} PackProductSummary
 * @property {number} total_pack_products
 * @property {number} high_confidence
 * @property {number} medium_confidence
 * @property {number} low_confidence
 * @property {number} verified_count
 * @property {number} manual_override_count
 * @property {number} needs_review_count
 * @property {number} [total_pack_stock]
 * @property {number} [total_inferred_units]
 * @property {number} [negative_stock_count]
 */

/**
 * Pack Products API Service
 */
const packProductsService = {
    /**
     * Get dashboard statistics
     * @returns {Promise<{success: boolean, data: {summary: PackProductSummary, topPackSizes: Array}}>}
     */
    getStats: async () => {
        try {
            const response = await axios.get(`${PACK_PRODUCTS_API_URL}/stats`);
            return response.data;
        } catch (error) {
            console.error('Error fetching pack products stats:', error);
            throw error;
        }
    },

    /**
     * Get brands for filter dropdown
     * @returns {Promise<{success: boolean, data: Array<{brand_name: string, product_count: number}>}>}
     */
    getBrands: async () => {
        try {
            const response = await axios.get(`${PACK_PRODUCTS_API_URL}/brands`);
            return response.data;
        } catch (error) {
            console.error('Error fetching pack products brands:', error);
            throw error;
        }
    },

    /**
     * Get pack products list with filters and pagination
     * @param {Object} params - Query parameters
     * @param {number} [params.page=1] - Page number
     * @param {number} [params.pageSize=50] - Items per page (max 200)
     * @param {string} [params.sortBy='sample_size'] - Sort field
     * @param {'ASC'|'DESC'} [params.sortOrder='DESC'] - Sort direction
     * @param {'HIGH'|'MEDIUM'|'LOW'} [params.confidenceLevel] - Filter by confidence
     * @param {boolean} [params.verifiedOnly=false] - Only verified products
     * @param {boolean} [params.needsReview=false] - Only products needing review
     * @param {string} [params.search] - Search term
     * @param {string} [params.brand] - Filter by brand name
     * @returns {Promise<{success: boolean, data: {summary: PackProductSummary, products: PackProduct[], pagination: Object, filters: Object}}>}
     */
    getList: async (params = {}) => {
        try {
            const response = await axios.get(PACK_PRODUCTS_API_URL, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching pack products list:', error);
            throw error;
        }
    },

    /**
     * Get single pack product by ID
     * @param {number} id - Record ID (not product_id)
     * @returns {Promise<{success: boolean, data: PackProduct}>}
     */
    getById: async (id) => {
        try {
            const response = await axios.get(`${PACK_PRODUCTS_API_URL}/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching pack product by ID:', error);
            throw error;
        }
    },

    /**
     * Update a pack product
     * @param {number} id - Record ID (not product_id)
     * @param {Object} data - Update data
     * @param {number|null} [data.manual_pack_size] - Manual override (null to clear)
     * @param {boolean} [data.is_verified] - Mark as verified
     * @param {string} [data.verified_by] - Verifier name
     * @param {string} [data.notes] - Admin notes
     * @returns {Promise<{success: boolean, message: string, data: PackProduct}>}
     */
    update: async (id, data) => {
        try {
            const response = await axios.put(`${PACK_PRODUCTS_API_URL}/${id}`, data);
            return response.data;
        } catch (error) {
            console.error('Error updating pack product:', error);
            throw error;
        }
    },

    /**
     * Add a new product to Pack Manager (for products discovered via hybrid search)
     * @param {Object} data - Product data
     * @param {string} data.product_id - The ProductId from Products table
     * @param {number} data.manual_pack_size - Pack size (must be >= 1)
     * @param {string} [data.notes] - Optional notes about the entry
     * @returns {Promise<{success: boolean, message: string, data: PackProduct}>}
     */
    create: async (data) => {
        try {
            const response = await axios.post(PACK_PRODUCTS_API_URL, data);
            return response.data;
        } catch (error) {
            console.error('Error creating pack product:', error);
            throw error;
        }
    },

    /**
     * Bulk verify multiple products
     * @param {number[]} ids - Array of record IDs
     * @param {string} [verifiedBy='Admin'] - Verifier name
     * @returns {Promise<{success: boolean, message: string, data: {updatedCount: number, requestedIds: number[]}}>}
     */
    bulkVerify: async (ids, verifiedBy = 'Admin') => {
        try {
            const response = await axios.post(`${PACK_PRODUCTS_API_URL}/bulk-verify`, {
                ids,
                verified_by: verifiedBy,
            });
            return response.data;
        } catch (error) {
            console.error('Error bulk verifying pack products:', error);
            throw error;
        }
    },

    /**
     * Refresh pack products inference from sales data
     * @param {Object} [options]
     * @param {number} [options.minSampleSize=3] - Minimum sales required
     * @param {boolean} [options.updateExisting=false] - Update unverified products
     * @returns {Promise<{success: boolean, message: string, data: {products_inserted: number, products_updated: number, total_pack_products: number}}>}
     */
    refresh: async (options = {}) => {
        try {
            const response = await axios.post(`${PACK_PRODUCTS_API_URL}/refresh`, options);
            return response.data;
        } catch (error) {
            console.error('Error refreshing pack products inference:', error);
            throw error;
        }
    },

    // =========================================================================
    // UNIFIED STOCK ENDPOINTS
    // =========================================================================

    /**
     * Get unified stock statistics
     * @returns {Promise<{success: boolean, data: {summary: Object, packSizeDistribution: Array}}>}
     */
    getUnifiedStockStats: async () => {
        try {
            const response = await axios.get(`${PACK_PRODUCTS_API_URL}/unified-stock/stats`);
            return response.data;
        } catch (error) {
            console.error('Error fetching unified stock stats:', error);
            throw error;
        }
    },

    /**
     * Get unified stock list with filters and pagination
     * @param {Object} params - Query parameters
     * @param {number} [params.page=1] - Page number
     * @param {number} [params.pageSize=50] - Items per page (max 200)
     * @param {string} [params.sortBy='calculated_qty'] - Sort field
     * @param {'ASC'|'DESC'} [params.sortOrder='DESC'] - Sort direction
     * @param {boolean} [params.includeZeroStock=false] - Include products with 0 stock
     * @param {boolean} [params.packProductsOnly=false] - Only show pack products
     * @param {boolean} [params.nonPackOnly=false] - Only show non-pack products
     * @param {boolean} [params.verifiedOnly=false] - Only show verified products
     * @param {string} [params.search] - Search by code or description
     * @param {string} [params.brand] - Filter by brand name
     * @returns {Promise<{success: boolean, data: {summary: Object, products: Array, pagination: Object, filters: Object}}>}
     */
    getUnifiedStock: async (params = {}) => {
        try {
            const response = await axios.get(`${PACK_PRODUCTS_API_URL}/unified-stock`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching unified stock list:', error);
            throw error;
        }
    },
};

export default packProductsService;
